import c from 'classnames';
import arrayMutators from 'final-form-arrays';
import { useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useHistory } from 'react-router-dom';
import { PageWizardProps } from '.';
import Toggle from '../../../common/Toggle';
import AdvancedSettingsToggle from '../../../components/AdvancedSettingsToggle';
import Error from '../../../components/Error';
import { FormSection, FormSectionBody, FormSectionHeader, StyledForm } from '../../../components/Form';
import AutoSave from '../../../components/FormAutoSave';
import Info from '../../../components/Info';
import { Pill } from '../../../components/Pill';
import { ROUTES } from '../../../routes/routes';
import { PaymentMethod, Plan } from '../../../types/pricingPage';
import VALIDATORS from '../../../utils/validators';
import Footer from './Footer';
import { withPageIdParam } from './utils';

const REQUIRED_FIELDS_BY_PAYMENT_METHOD: Map<PaymentMethod, string[]> = new Map();
REQUIRED_FIELDS_BY_PAYMENT_METHOD.set('afterpay_clearpay', ['paymentSettings.requireShippingAddress']);

interface Props extends PageWizardProps {
    onChangePlans: (plans: Plan[]) => Promise<void>;
}

export default function ChooseFlow({ onChangePlans, page }: Props) {
    const [showAdvanced, setShowAdvanced] = useState(false);
    const handleToggleShowAdvanced = () => setShowAdvanced(!showAdvanced);
    const history = useHistory();
    // FormAutoSave means we don't use the form submission process so store errors manually here
    const [submitError, setSubmitError] = useState<string | undefined>();

    const save = async ({ paymentFlow, paymentSettings, subscriptionSettings }: Plan) => {
        setSubmitError(undefined);
        try {
            await onChangePlans(
                page.plans.map((plan) => ({ ...plan, paymentFlow, paymentSettings, subscriptionSettings }))
            );
        } catch (err) {
            setSubmitError('Error Saving.');
        }
    };

    if (page.plans.length < 1) {
        history.push(withPageIdParam(ROUTES.PAGES_NEW, page.id));
    }

    //TODO: allow setting payment flow for individual plans
    const firstPlan = page.plans[0];

    const ready =
        firstPlan.paymentFlow.returnToOriginSite ||
        (firstPlan.paymentFlow.paymentSuccessUrl && firstPlan.paymentFlow.paymentFailureUrl);

    return (
        <div className="grid grid-cols-1 gap-4 px-4">
            <div className="font-bold text-center">Configure Checkout</div>
            <div>
                <Form<Plan> onSubmit={save} initialValues={firstPlan} mutators={{ ...arrayMutators }}>
                    {({ values, handleSubmit, form }) => (
                        <StyledForm onSubmit={handleSubmit}>
                            <FormSection>
                                <FormSectionHeader>Checkout</FormSectionHeader>
                                <FormSectionBody>
                                    <div className="mt-2">
                                        <Field name="paymentSettings.requireBillingAddress" type="checkbox">
                                            {(props) => (
                                                <label className="inline-flex items-center">
                                                    <Toggle {...props.input} />
                                                    <span className="ml-2 text-sm text-gray-600">
                                                        Require customer Billing address?
                                                    </span>
                                                </label>
                                            )}
                                        </Field>
                                    </div>
                                    <div className="mt-2 mb-4">
                                        <Field name="paymentSettings.requireShippingAddress" type="checkbox">
                                            {(props) => (
                                                <label className="inline-flex items-center">
                                                    <Toggle {...props.input} />
                                                    <span className="ml-2 text-sm text-gray-600">
                                                        Require customer Shipping address?
                                                    </span>
                                                </label>
                                            )}
                                        </Field>
                                    </div>

                                    <p className="mb-4 text-sm">
                                        Payment methods can be configured in the{' '}
                                        <a
                                            href={`https://dashboard.stripe.com${
                                                page.status !== 'ACTIVE' ? '/test' : ''
                                            }/settings/payment_methods`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="link"
                                        >
                                            Stripe Dashboard
                                        </a>
                                    </p>
                                </FormSectionBody>
                            </FormSection>

                            <hr />
                            <FormSection>
                                <FormSectionHeader>After Checkout</FormSectionHeader>
                                <FormSectionBody>
                                    <div className="mb-4">
                                        <Field name="paymentFlow.returnToOriginSite" type="checkbox">
                                            {(props) => (
                                                <label className="inline-flex items-center">
                                                    <Toggle {...props.input} />
                                                    <span
                                                        className={c('ml-2 text-gray-600 text-sm', {
                                                            'line-through': !values.paymentFlow.returnToOriginSite,
                                                        })}
                                                    >
                                                        Return to the page you came from
                                                    </span>
                                                </label>
                                            )}
                                        </Field>
                                    </div>
                                    {!values.paymentFlow.returnToOriginSite && (
                                        <>
                                            <div className="mb-4">
                                                <label className="text-sm text-gray-600 mb-2">
                                                    <span className="block">Success Page</span>
                                                    <span className="block text-sm text-gray-600">
                                                        Where should the customer be taken after successful payment?
                                                    </span>
                                                </label>

                                                <Field
                                                    name="paymentFlow.paymentSuccessUrl"
                                                    component="input"
                                                    type="url"
                                                    validate={VALIDATORS.requiredUrl}
                                                >
                                                    {({ input, meta }) => (
                                                        <>
                                                            <input
                                                                {...input}
                                                                placeholder="e.g. https://YOUR-APP/payment/success"
                                                                className="input w-full"
                                                            />
                                                            {meta.error && meta.touched && (
                                                                <span className="ml-2">
                                                                    <Error message={meta.error} />
                                                                </span>
                                                            )}
                                                        </>
                                                    )}
                                                </Field>
                                                <div className="text-sm my-4">
                                                    <Pill title="Tip" />{' '}
                                                    <span className="ml-1">You can use placeholders </span>
                                                    <div className="mb-2">
                                                        <pre className="inline">{'{{stripePriceId}}'}</pre> or{' '}
                                                        <pre className="inline">{'{{stripeProductId}}'}</pre>
                                                    </div>
                                                    Example:{' '}
                                                    <span>
                                                        https://myapp/payment/success?product=
                                                        {'{{stripeProductId}}'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="inline-block text-sm text-gray-600 mb-2">
                                                    <span className="block">Cancel Page</span>
                                                    <span className="block text-sm text-gray-600">
                                                        Where should the customer be taken after they cancel payment?
                                                    </span>
                                                </label>
                                                <Field
                                                    name="paymentFlow.paymentFailureUrl"
                                                    component="input"
                                                    type="url"
                                                    validate={VALIDATORS.requiredUrl}
                                                >
                                                    {({ input, meta }) => (
                                                        <>
                                                            <input
                                                                {...input}
                                                                placeholder="e.g. https://YOUR-APP/payment/cancel"
                                                                className="input w-full"
                                                            />
                                                            {meta.error && meta.touched && (
                                                                <span className="ml-2">
                                                                    <Error message={meta.error} />
                                                                </span>
                                                            )}
                                                        </>
                                                    )}
                                                </Field>
                                            </div>
                                        </>
                                    )}
                                </FormSectionBody>
                            </FormSection>

                            <hr />

                            <FormSection>
                                <FormSectionHeader>Advanced</FormSectionHeader>
                                <FormSectionBody>
                                    <AdvancedSettingsToggle value={showAdvanced} onChange={handleToggleShowAdvanced} />
                                    <div className={c({ hidden: !showAdvanced })}>
                                        <AdvancedSettings values={values} />
                                    </div>
                                    <div className="flex pb-2 pr-2">
                                        {submitError && <Error message={submitError} />}
                                        <AutoSave debounce={1000} save={save} />
                                    </div>
                                </FormSectionBody>
                            </FormSection>
                        </StyledForm>
                    )}
                </Form>
            </div>
            <Footer
                error={!ready ? 'Success and Failure urls are required' : undefined}
                prev={{ href: ROUTES.PAGES_NEW, disabled: false }}
                next={{ href: ROUTES.PAGES_NEW_FEATURES, disabled: !ready }}
                item={page}
            />
        </div>
    );
}

function AdvancedSettings(props: { values: Plan }) {
    return (
        <>
            <div className="mb-2">
                <Field name="subscriptionSettings.sendInvoice" type="checkbox">
                    {(props) => (
                        <>
                            <label className="inline-flex items-center w-full">
                                <Toggle {...props.input} />
                                <span className="ml-2 text-sm text-gray-600">Send an invoice after payment?</span>
                            </label>
                            {props.input.checked && (
                                <label className="inline-flex items-center">
                                    <span className="mr-2 text-sm text-gray-600">
                                        Make sure you have "Send emails after successful payments" is enabled in Stripe{' '}
                                        <a
                                            href="https://dashboard.stripe.com/settings/emails "
                                            className="link"
                                            target="_blank"
                                            rel="noreferrer noopener"
                                        >
                                            settings
                                        </a>
                                    </span>
                                </label>
                            )}
                        </>
                    )}
                </Field>
            </div>
            <div className="mb-2">
                <Field name="subscriptionSettings.embeddedUi" type="checkbox">
                    {(props) => (
                        <label className="inline-flex items-center w-full">
                            <Toggle {...props.input} />
                            <span className="ml-2 text-sm text-gray-600">Enable Embedded Checkout?</span>
                        </label>
                    )}
                </Field>
            </div>
            <div className="mb-2">
                <Field name="subscriptionSettings.trialDays" type="number">
                    {({ input: { onChange, ...inputProps } }) => (
                        <>
                            <label className="inline-flex items-center w-full">
                                <Toggle
                                    name={inputProps.name}
                                    checked={!!inputProps.value}
                                    onChange={(checked) => onChange(checked ? 30 : undefined)}
                                />
                                <span className="ml-2 text-sm text-gray-600">Free trial?</span>
                            </label>
                            {!!inputProps.value && (
                                <label className="inline-flex items-center ml-4">
                                    <span className="mr-2 text-sm text-gray-600">Free trial for</span>
                                    <input
                                        onChange={onChange}
                                        {...inputProps}
                                        className="input w-16"
                                        min={0}
                                        placeholder=""
                                    />
                                    <span className="ml-2 text-sm text-gray-600">days</span>
                                </label>
                            )}
                        </>
                    )}
                </Field>
                {props.values.subscriptionSettings.trialDays && (
                    <div className="ml-4 mt-2">
                        <div className="mb-2">
                            <Field name="subscriptionSettings.trialSettings.oncePerCustomer" type="checkbox">
                                {(props) => (
                                    <label className="inline-flex items-center w-full">
                                        <Toggle {...props.input} />
                                        <span className="ml-2 text-sm text-gray-600">
                                            One free trial per customer?
                                            <Info
                                                className="text-black ml-1"
                                                info="This will prevent customers from signing up for multiple free trials. It uses the customer's email address to track this."
                                            />
                                        </span>
                                    </label>
                                )}
                            </Field>
                        </div>
                        <div className="mb-2">
                            <Field name="subscriptionSettings.trialSettings.skipPayment" type="checkbox">
                                {(props) => (
                                    <label className="inline-flex items-center w-full">
                                        <Toggle {...props.input} />
                                        <span className="ml-2 text-sm text-gray-600">
                                            Skip credit card collection before trial?
                                        </span>
                                    </label>
                                )}
                            </Field>
                        </div>
                    </div>
                )}
            </div>
            <div className="mb-2">
                <Field name="subscriptionSettings.adjustableQuantity" type="checkbox">
                    {(props) => (
                        <>
                            <label className="inline-flex items-center w-full">
                                <Toggle {...props.input} />
                                <span className="ml-2 text-sm text-gray-600">Enable Adjustable Quantity?</span>
                            </label>
                            {props.input.checked && (
                                <label className="inline-flex items-center">
                                    <span className="mr-2 text-sm text-gray-600">
                                        Quantity can be adjusted at checkout (min: 0, max: 99). <br /> You can use the{' '}
                                        <pre className="inline">data-quantity</pre> attribute to specify an initial
                                        quantity.
                                    </span>
                                </label>
                            )}
                        </>
                    )}
                </Field>
            </div>
            <div className="mb-2">
                <Field name="subscriptionSettings.coupons" type="checkbox">
                    {(props) => (
                        <>
                            <label className="inline-flex items-center w-full">
                                <Toggle {...props.input} />
                                <span className="ml-2 text-sm text-gray-600">Enable Coupon Codes?</span>
                            </label>
                            {props.input.checked && (
                                <label className="inline-flex items-center">
                                    <span className="mr-2 text-sm text-gray-600">
                                        <a
                                            href="https://dashboard.stripe.com/test/coupons"
                                            target="_blank"
                                            rel="noreferrer noopener"
                                            className="link"
                                        >
                                            Coupon codes
                                        </a>{' '}
                                        can be entered at checkout.
                                    </span>
                                </label>
                            )}
                        </>
                    )}
                </Field>
            </div>
            <div className="mb-2">
                <Field name="subscriptionSettings.automaticTax" type="checkbox">
                    {(props) => (
                        <label className="inline-flex items-center w-full">
                            <Toggle {...props.input} />
                            <span className="ml-2 text-sm text-gray-600">
                                Enable Stripe Tax?{' '}
                                <Info
                                    className="text-black"
                                    href="https://stripe.com/in/tax"
                                    info="See Stripe Tax docs"
                                />
                            </span>
                        </label>
                    )}
                </Field>
            </div>
            <div className="mb-2">
                <Field name="subscriptionSettings.collectTaxId" type="checkbox">
                    {(props) => (
                        <label className="inline-flex items-center w-full">
                            <Toggle {...props.input} />
                            <span className="ml-2 text-sm text-gray-600">
                                Collect Tax ID for businesses?{' '}
                                <Info
                                    className="text-black"
                                    info={`When checked, customers will be asked "Are you a business?" and if they check yes they will be asked for their Tax ID. This is required for some countries.`}
                                />
                            </span>
                        </label>
                    )}
                </Field>
            </div>
            <div className="mb-2">
                <Field name="subscriptionSettings.skipPayment.enabled" type="checkbox">
                    {(props) => (
                        <label className="inline-flex items-center w-full">
                            <Toggle {...props.input} />
                            <span className="ml-2 text-sm text-gray-600">
                                Skip credit card collection for FREE products?{' '}
                                <Info
                                    className="text-black"
                                    info="When you pass an email address to your pricing page using data-email, PriceWell will create a Stripe customer with that email and sign them up to the plan without going through checkout."
                                />
                            </span>
                        </label>
                    )}
                </Field>
            </div>
            <div className="mb-2">
                <Field name="subscriptionSettings.enableAddons" type="checkbox">
                    {(props) => (
                        <label className="inline-flex items-center w-full">
                            <Toggle {...props.input} />
                            <span className="ml-2 text-sm text-gray-600">
                                Enable Addon Products?{' '}
                                <Info
                                    className="text-black"
                                    info="When enabled we'll check your Stripe Products for metadata with the name 'pricewell_addons' containing a comma-separated list of Stripe Price ids to add add checkout"
                                />
                            </span>
                        </label>
                    )}
                </Field>
            </div>
            <div className="mb-2">
                <Field name="subscriptionSettings.expireAfterPeriods" type="number">
                    {({ input: { onChange, ...inputProps } }) => (
                        <>
                            <label className="inline-flex items-center w-full">
                                <Toggle
                                    name={inputProps.name}
                                    checked={!!inputProps.value}
                                    onChange={(checked) => onChange(checked ? 12 : undefined)}
                                />
                                <span className="ml-2 text-sm text-gray-600">
                                    Subscription expiry date?{' '}
                                    <Info info="Automatically cancel a subscription after a certain number of periods (e.g. months for a monthly subscription)" />
                                </span>
                            </label>
                            {!!inputProps.value && (
                                <label className="inline-flex items-center">
                                    <span className="mr-2 text-sm text-gray-600">Expire subscription after</span>
                                    <input
                                        onChange={onChange}
                                        {...inputProps}
                                        className="input w-16"
                                        min={0}
                                        placeholder=""
                                    />
                                    <span className="ml-2 text-sm text-gray-600">periods (this is usually months)</span>
                                </label>
                            )}
                        </>
                    )}
                </Field>
            </div>
            <div className="mb-2">
                <Field name="subscriptionSettings.chargeLater" type="checkbox">
                    {(props) => (
                        <label className="inline-flex items-center w-full">
                            <Toggle {...props.input} />
                            <span className="ml-2 text-sm text-gray-600">
                                Delay payment for future use?{' '}
                                <Info info="When enabled Stripe Checkout will be launched in 'Setup' mode which means we'll collect card details, create a customer but not charge them." />
                            </span>
                        </label>
                    )}
                </Field>
            </div>
            <div className="mb-2">
                <Field name="subscriptionSettings.resetBillingCycle.enabled" type="checkbox">
                    {(props) => (
                        <label className="inline-flex items-center w-full">
                            <Toggle {...props.input} />
                            <span className="ml-2 text-sm text-gray-600">
                                Delay payment until the 1st of the month.
                                <Info info="Applies to monthly products. Useful if you run a membership club. A coupon will be applied that gives a free trial for the remainder of the current month. i.e. 5 day trial if there are 5 days of the month left." />
                            </span>
                        </label>
                    )}
                </Field>
            </div>
        </>
    );
}
